/* eslint-disable */
import { TrainingData } from '../../models';
import {
  DRTCS_FRAMEWORK,
  DRTCS_LEVEL_MODEL,
  DRTCS_LEVELS_ORDER,
  DRTCS_LEVEL_VALUE,
  DRTCS_TIERS,
} from '../prototype/training-data';

/** ISO date `d` days before today (seed runs once at load). */
const iso = (d: number) => new Date(Date.now() - d * 86_400_000).toISOString().slice(0, 10);

/**
 * Seed the DRTCS training state. Gives every competency a perform cadence (how
 * often it must be done) and a supervisor-review cadence, one set of demo materials,
 * supervisor-set levels for Joe and Ben, and some perform history so the due /
 * overdue states show variety. Level values (`level`) are set only by a supervisor.
 */
function buildTrainingSeed(): TrainingData {
  // A little cadence variety across competencies so the demo isn't flat.
  const performCadences = ['weekly', 'daily', 'monthly', 'weekly', 'quarterly', 'annually'];
  const framework: any[] = (DRTCS_FRAMEWORK as any[]).map((p) => ({
    ...p,
    categories: p.categories.map((c: any, ci: number) => ({
      ...c,
      items: c.items.map((i: any, ii: number) => ({
        ...i,
        performCadence: i.performCadence ?? performCadences[(ci + ii) % performCadences.length],
        reviewCadence: i.reviewCadence ?? 'monthly',
      })),
    })),
  }));

  // How long ago each seeded record was last performed → up-to-date / due / overdue.
  const perfDaysAgo = [0, 2, 12, 40];

  // --- Realistic performance history --------------------------------------------
  // So "done ÷ required to date" reconciles with tenure (no more "7 of 1"). Anchor on
  // each person's real start date; done ≈ a fraction of what was required (some behind),
  // plus a few N/A periods. (Interim seed math — the backend will own the real counts.)
  const todayISO = iso(0);
  const CAD_DAYS: Record<string, number> = { daily: 1, weekly: 7, monthly: 30, quarterly: 91, annually: 365 };
  const expectedSince = (cadence: string | undefined, startISO: string): number => {
    if (!cadence || cadence === 'as-needed') return 0;
    const days = Math.max(0, Math.round((Date.parse(todayISO) - Date.parse(startISO)) / 86_400_000));
    if (cadence === 'daily') return Math.round((days * 4) / 7); // ~4 scheduled workdays/week
    return Math.floor(days / (CAD_DAYS[cadence] ?? 7)) + 1;
  };
  /** Realistic {performCount, naCount} for a competency given its cadence + start date. */
  const history = (cadence: string | undefined, startISO: string, idx: number): { performCount: number; naCount: number } => {
    const exp = expectedSince(cadence, startISO);
    if (!exp) return { performCount: 2 + (idx % 4), naCount: 0 }; // as-needed: a few times
    const ratio = [0.92, 0.78, 1, 0.85, 0.6][idx % 5]; // mostly keeping up, some behind
    const naCount = [0, 1, 0, 2, 3][idx % 5];
    return { performCount: Math.max(0, Math.round(exp * ratio) - naCount), naCount };
  };
  // Real start dates (mirror EMPLOYEE_START_DATES) so tenure lines up with levels.
  const BEN_START = '2024-06-15';
  const JOE_START = '2024-01-15';

  const safety = framework.find((pl) => pl.id === 'safety');
  if (safety) {
    safety.attachments = [
      { id: 'safety-a-seed1', kind: 'video', name: 'OSHA 10 Orientation (intro video)', url: 'https://www.osha.gov/' },
      { id: 'safety-a-seed2', kind: 'file', name: 'Jobsite Safety Plan — template.pdf', url: '' },
    ];
  }

  const levelValue = DRTCS_LEVEL_VALUE as Record<string, number>;
  const levelsOrder = DRTCS_LEVELS_ORDER as string[];

  // Ben — assigned all six modules with supervisor-set levels (~20% unassessed).
  const seedLevels: Record<string, string> = { conduct: 'L4', safety: 'L3', projdisc: 'L3', digital: 'L2', trade: 'L3', leadership: 'L2' };
  const ben: Record<string, any> = {};
  framework.forEach((pl) => {
    const base = levelValue[seedLevels[pl.id]] || 0;
    pl.categories.forEach((c: any) =>
      c.items.forEach((it: any, idx: number) => {
        if (idx % 5 === 4) return;
        const v = Math.max(1, base - (idx % 5 === 0 ? 1 : 0));
        const bh = history(it.performCadence, BEN_START, idx);
        ben[it.id] = { level: levelsOrder[v - 1], by: 'Joseph Kingston', at: '2026-06-18', lastPerformedAt: iso(perfDaysAgo[idx % 4]), performCount: bh.performCount, naCount: bh.naCount, lastReviewedAt: idx % 4 === 1 ? iso(6) : null };
      }),
    );
  });

  // Joe — assigned all six modules, fully populated example.
  const joeLevels: Record<string, string> = { conduct: 'L5', safety: 'L4', projdisc: 'L4', digital: 'L4', trade: 'L4', leadership: 'L5' };
  const joe: Record<string, any> = {};
  framework.forEach((pl) => {
    const base = levelValue[joeLevels[pl.id]] || 0;
    pl.categories.forEach((c: any) =>
      c.items.forEach((it: any, idx: number) => {
        if (idx % 6 === 5) return;
        const v = Math.max(1, base - (idx % 4 === 0 ? 1 : 0));
        const jh = history(it.performCadence, JOE_START, idx);
        joe[it.id] = { level: levelsOrder[v - 1], by: 'Self / Board', at: '2026-06-15', lastPerformedAt: iso(perfDaysAgo[(idx + 1) % 4]), performCount: jh.performCount, naCount: jh.naCount, lastReviewedAt: idx % 3 === 0 ? iso(4) : null };
      }),
    );
  });

  // Seed the review flow: one submitted competency + one with a note back.
  if (ben['conduct-c1-i1']) {
    const tv = Math.min(5, (levelValue[ben['conduct-c1-i1'].level] || 0) + 1);
    ben['conduct-c1-i1'] = { ...ben['conduct-c1-i1'], lastPerformedAt: iso(1), performCount: 9, submitted: true, submittedAt: '2026-06-22', targetLevel: levelsOrder[tv - 1] };
  }
  if (ben['conduct-c1-i2'])
    ben['conduct-c1-i2'] = { ...ben['conduct-c1-i2'], notes: 'Strong progress. Hold on-site-ready 10 minutes early for two full weeks, then resubmit and I’ll move you up.', noteBy: 'Joseph Kingston', noteAt: '2026-06-19' };
  // A coaching note on Joe too, so the default persona's Supervisor Feedback section has content.
  if (joe['safety-c1-i1'])
    joe['safety-c1-i1'] = { ...joe['safety-c1-i1'], notes: 'Foundational safety is rock solid — keep the daily checks logged and you are set for Trainer level.', noteBy: 'Board', noteAt: iso(3), lastReviewedAt: iso(3) };

  // 'Cert' is a FLAG, as on the server (contract §2) — not a field of its own — so
  // the prototype's `cert: true` competencies carry it the same way.
  const customFlags = [
    { id: 'flag-cert', label: 'Cert', color: null, desc: 'Requires a current certification or card on file.' },
    { id: 'flag-recert', label: 'Recert Annually', color: null, desc: 'Must be re-certified every 12 months.' },
  ];
  framework.forEach((pl) =>
    pl.categories.forEach((c: any) =>
      c.items.forEach((it: any) => {
        if (it.cert) it.flags = { ...(it.flags ?? {}), 'flag-cert': true };
        delete it.cert;
        if (it.id === 'safety-c1-i1') it.flags = { ...(it.flags ?? {}), 'flag-recert': true };
      }),
    ),
  );

  return {
    framework,
    customFlags,
    completions: { ben, joe },
    defaultReviewReps: 50,
    assignments: { ben: framework.map((pl) => pl.id), joe: framework.map((pl) => pl.id) },
    levels: DRTCS_LEVEL_MODEL as any[],
    tiers: DRTCS_TIERS as any[],
  } as TrainingData;
}

export const TRAINING_SEED: TrainingData = buildTrainingSeed();
